/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { FeedItemType, OfferType } from 'client-code/wrs-feed-service';

import { PreviewComponent } from '../../../core/usecases';
import { DateTime } from '../../../core/utils';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface FormattedPreview {
    title: string;
    subtitle: string;
    ratings: string[];
    channelName?: string;
    dayLabel?: string;
    timeRange?: string;
    showRatings: boolean;
}

const TITLE_NOT_AVAILABLE = 'Title Not Available';

const DESCRIPTION_NOT_AVAILABLE = 'Description Not Available';

const EMPTY_PREVIEW: FormattedPreview = {
    title: TITLE_NOT_AVAILABLE,
    subtitle: '',
    ratings: [],
    showRatings: false,
};

const SPORTS_FEED_ITEM_TYPES: string[] = [
    FeedItemType.SportsEvent,
    FeedItemType.SportsTeam,
    FeedItemType.SportsOrganization,
];

// ─── Shared helpers ─────────────────────────────────────────────────────────

/**
 * Parses airing times into separate day-label and time-range strings.
 * - dayLabel:  "Today" or "Tue 3/6"
 * - timeRange: "6:00-7:30pm" or "6:00pm"
 */
const formatAiringTime = (
    startTime?: string,
    endTime?: string,
): { dayLabel: string; timeRange: string } => {
    if (!startTime) return { dayLabel: '', timeRange: '' };

    const start = new Date(startTime);
    if (Number.isNaN(start.getTime())) return { dayLabel: '', timeRange: '' };

    const now = new Date();
    const isToday =
        start.getFullYear() === now.getFullYear() &&
        start.getMonth() === now.getMonth() &&
        start.getDate() === now.getDate();

    const dayLabel = isToday
        ? 'Today'
        : `${start.toLocaleDateString('en-US', { weekday: 'short' })} ${start.getMonth() + 1}/${start.getDate()}`;

    const endDate = endTime ? new Date(endTime) : null;

    const startStr = new DateTime(start).to12HourClock();
    const endStr = endDate ? new DateTime(endDate).to12HourClock() : '';
    const timeRange = endStr ? `${startStr} - ${endStr}` : startStr;

    return { dayLabel, timeRange };
};

/**
 * Builds the channel display string, e.g. "ESPN(206)".
 * Returns empty string if callSign is not available (won't display stationId).
 */
const formatChannelName = (channel?: { callSign?: string; channelNumber?: string }): string => {
    if (!channel?.callSign) return '';
    const channelNum = channel.channelNumber ? `(${channel.channelNumber})` : '';
    return `${channel.callSign}${channelNum}`;
};

/**
 * Resolves internal rating objects to the single highest-level rating name
 * (e.g. "TV-MA" rather than all ratings). Uses PreviewComponent.getHighestParentalRatingName.
 */
const resolveRatingNames = (
    internalRatingList?: { typeId?: string; valueId?: string }[],
): string[] => {
    if (!internalRatingList?.length) return [];
    const highest = PreviewComponent.getHighestParentalRatingName(internalRatingList);
    return highest ? [highest] : [];
};

// ─── Per-type formatters ────────────────────────────────────────────────────

/**
 * Episode format:
 *   Title   → series title
 *   Subtitle→ "S5 :E14 - Episode Title"
 *   Ratings → shown
 */
const formatEpisodePreview = (contentPreview: any): FormattedPreview => {
    if (!contentPreview.title) contentPreview.title = TITLE_NOT_AVAILABLE;
    const { seasonNumber, episodeNumber, episodeTitle } = contentPreview;
    const seParts: string[] = [];
    if (seasonNumber != null || episodeNumber != null) {
        const se = [
            seasonNumber != null ? `S${seasonNumber}` : '',
            episodeNumber != null ? `E${episodeNumber}` : '',
        ]
            .filter(Boolean)
            .join(' :');
        seParts.push(se);
    }
    if (episodeTitle) {
        seParts.push(episodeTitle);
    }

    const airing = formatAiringTime(contentPreview.startTime, contentPreview.endTime);

    return {
        title: contentPreview.title ?? '',
        subtitle: seParts.join(' - '),
        ratings: resolveRatingNames(contentPreview.internalRatingList),
        channelName: formatChannelName(contentPreview.channel),
        dayLabel: airing.dayLabel,
        timeRange: airing.timeRange,
        showRatings: true,
    };
};

/**
 * Live Movie format:
 *   Title   → movie title
 *   Subtitle→ "2002 · Fantasy"  (year · genre)
 *   Ratings → badge row shown
 */
const formatLiveMoviePreview = (contentPreview: any): FormattedPreview => {
    if (!contentPreview.title) contentPreview.title = TITLE_NOT_AVAILABLE;
    const parts: string[] = [];
    if (contentPreview.movieYear) parts.push(String(contentPreview.movieYear));
    if (contentPreview.categoryLabel) {
        const genres = contentPreview.categoryLabel
            .split(',')
            .slice(0, 2)
            .map((g: string) => g.trim())
            .filter(Boolean)
            .join(', ');
        if (genres) parts.push(genres);
    }

    const airing = formatAiringTime(contentPreview.startTime, contentPreview.endTime);

    return {
        title: contentPreview.title ?? '',
        subtitle: parts.join(' · '),
        ratings: resolveRatingNames(contentPreview.internalRatingList),
        channelName: formatChannelName(contentPreview.channel),
        dayLabel: airing.dayLabel,
        timeRange: airing.timeRange,
        showRatings: true,
    };
};

/**
 * Non-live Movie format (VOD / Recording):
 *   Title   → movie title
 *   Subtitle→ "2024 · TV-G"  (year · first resolved rating)
 *   Ratings → NOT shown as badge (already in subtitle)
 */
const formatMoviePreview = (contentPreview: any): FormattedPreview => {
    if (!contentPreview.title) contentPreview.title = TITLE_NOT_AVAILABLE;
    const ratingNames = resolveRatingNames(contentPreview.internalRatingList);
    const parts: string[] = [];
    if (contentPreview.movieYear) parts.push(String(contentPreview.movieYear));
    if (ratingNames.length > 0) parts.push(ratingNames[0]);

    return {
        title: contentPreview.title ?? '',
        subtitle: parts.join(' · '),
        ratings: [],
        showRatings: false,
    };
};

/**
 * Sports format:
 *   Title   → series title
 *   Subtitle→ episode title
 *   Ratings → NOT shown
 */
const formatSportsPreview = (contentPreview: any): FormattedPreview => {
    if (!contentPreview.title) contentPreview.title = TITLE_NOT_AVAILABLE;
    const airing = formatAiringTime(contentPreview.startTime, contentPreview.endTime);

    return {
        title: contentPreview.title ?? '',
        subtitle: contentPreview.episodeTitle ?? '',
        ratings: [],
        channelName: formatChannelName(contentPreview.channel),
        dayLabel: airing.dayLabel,
        timeRange: airing.timeRange,
        showRatings: false,
    };
};

/**
 * Series format:
 *   Title   → series title
 *   Subtitle→ "Show · TV-14" (categoryLabel and rating on same line)
 *   Ratings → no badge row (rating is in subtitle)
 */
const formatSeriesPreview = (contentPreview: any): FormattedPreview => {
    const contentType = contentPreview.categoryLabel ?? '';
    const ratings = resolveRatingNames(contentPreview.internalRatingList);
    const parts = [contentType, ratings[0]].filter(Boolean);
    const subtitle = parts.join(' · ');

    return {
        title: contentPreview.title ?? '',
        subtitle,
        ratings: [],
        showRatings: false,
    };
};

// ─── Main dispatcher ────────────────────────────────────────────────────────

/**
 * Routes to the correct per-type formatter based on `feedItemType`.
 * Returns a flat `FormattedPreview` ready for TileDetailPanel to render.
 */
export const formatPreview = (previewData: any, feedItemTitle?: string): FormattedPreview => {
    const contentPreview = previewData?.contentPreview;
    if (!contentPreview) {
        if (feedItemTitle) {
            return {
                title: feedItemTitle,
                subtitle: DESCRIPTION_NOT_AVAILABLE,
                ratings: [],
                showRatings: false,
            };
        }
        return EMPTY_PREVIEW;
    }

    const feedItemType: string = previewData?.feedItemType ?? '';

    // Series tiles have specific formatting with title, "Series" label, and rating
    if (feedItemType === FeedItemType.Series) {
        return formatSeriesPreview(contentPreview);
    }

    if (SPORTS_FEED_ITEM_TYPES.includes(feedItemType)) {
        return formatSportsPreview(contentPreview);
    }

    if (feedItemType === FeedItemType.Movie) {
        return previewData?.offerType === OfferType.Live
            ? formatLiveMoviePreview(contentPreview)
            : formatMoviePreview(contentPreview);
    }

    // Episode / default
    return formatEpisodePreview(contentPreview);
};
